import * as Sentry from "@sentry/react";
import config from "../config";

const { localurl, environmentName, sentryDsn } = config || {};

export const init = () => {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      new Sentry.BrowserTracing({
        tracePropagationTargets: [localurl],
      }),
      new Sentry.Replay(),
    ],
    environment: environmentName || "unknown",
    // Injected at build time by @sentry/vite-plugin from the git commit SHA.
    // Sentry uses this to look up the correct source maps for the deployed build.
    release: import.meta.env.SENTRY_RELEASE,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
  });
};

export const setSentryUser = async (user = null) => {
  if (user !== null && user?.email) {
    Sentry.setUser({
      username: user?.name,
      email: user?.email,
      ip_address: "{{auto}}",
    });
  }
};

// Convert error-like plain objects (e.g. serialized AxiosError) back to real Error instances.
// This prevents the JSON blob title problem: when instanceof Error fails on a bundled AxiosError,
// safeStringify produces {"name":"AxiosError","message":"..."} as the Sentry event title.
const normalizeToError = (value) => {
  if (value instanceof Error) return value;
  if (
    value &&
    typeof value === "object" &&
    typeof value.message === "string" &&
    (value.name || value.stack)
  ) {
    const err = new Error(value.message);
    err.name = value.name || "Error";
    if (value.stack) err.stack = value.stack;
    return err;
  }
  return null;
};

// --- Extract first caller stack frame outside logService ---
const getStackLocation = () => {
  const stackLines = new Error().stack?.split("\n") || [];
  const ignorePatterns = ["logService.js", "@sentry", "node_modules"];

  for (const line of stackLines) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("at ")) continue;
    if (ignorePatterns.some((pattern) => trimmed.includes(pattern))) continue;

    const withMethod = trimmed.match(/at (.+?) \((.+):(\d+):(\d+)\)/);
    if (withMethod) {
      return {
        method: withMethod[1],
        file: withMethod[2],
        line: withMethod[3],
        column: withMethod[4],
      };
    }

    const withoutMethod = trimmed.match(/at (.+):(\d+):(\d+)/);
    if (withoutMethod) {
      return {
        method: "anonymous",
        file: withoutMethod[1],
        line: withoutMethod[2],
        column: withoutMethod[3],
      };
    }
  }

  return {};
};

const safeStringify = (value) => {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return String(value);
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch (err) {
      return "[Unserializable Object]";
    }
  }
  return String(value);
};

const normalizePayload = (payload) => {
  if (!payload) return {};
  if (payload instanceof Error) {
    return { payloadError: payload.message };
  }
  if (typeof payload === "object" && !Array.isArray(payload)) {
    return payload;
  }
  return { payloadValue: payload };
};

// --- Map error messages to relevant titles ---
const getErrorTitle = (error) => {
  const msg = error instanceof Error ? error.message : safeStringify(error);

  if (msg.includes("401")) return "Unauthorized Request";
  if (msg.includes("403")) return "Forbidden Request";
  if (msg.includes("404")) return "Resource Not Found";
  if (msg.includes("500")) return "Server Error";
  if (msg.includes("Network Error")) return "Network Failure";

  return "Application Error";
};

const getLogTitle = (level) => {
  switch (level) {
    case "info":
      return "Information Log";
    case "warning":
      return "Warning Alert";
    case "debug":
      return "Debug Log";
    case "log":
      return "General Log";
    default:
      return "Application Log";
  }
};

// Apply API-specific Sentry tags when the payload contains HTTP context keys.
// This enables filtering in Sentry by endpoint, HTTP method, and status code.
const applyApiTags = (scope, payload) => {
  if (payload.method) scope.setTag("api.method", String(payload.method).toUpperCase());
  if (payload.status != null) scope.setTag("api.status", String(payload.status));
  if (payload.url) scope.setTag("api.endpoint", payload.url);
  if (payload.route) scope.setTag("route", payload.route);
  if (payload.component) scope.setTag("component", payload.component);
  if (payload.correlationId) scope.setTag("correlationId", payload.correlationId);
};

const logWithPayload = (message, payload = {}, user = null) => {
  // The HTTP interceptor in httpService.js already captured this error with
  // full API context. Skip it here to prevent a duplicate, lower-quality event
  // from component-level catch handlers that receive the same rejected promise.
  if (message?.__sentryLogged) return;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({
        username: user?.name,
        email: user?.email,
        ip_address: "{{auto}}",
      });
    }

    const normalizedPayload = normalizePayload(payload);
    if (Object.keys(normalizedPayload).length > 0) {
      // Group API context into structured Sentry contexts instead of flat extras
      const { method, url, baseURL, params, data, headers, responseData, status, route, component, correlationId, ...rest } = normalizedPayload;

      if (method || url || status != null) {
        scope.setContext("API Request", {
          method: method ? String(method).toUpperCase() : undefined,
          url: baseURL && url ? `${baseURL}${url}` : url,
          params,
          body: data,
        });
        scope.setContext("API Response", {
          status,
          data: responseData,
        });
        if (headers) {
          scope.setContext("Request Headers", headers);
        }
      }

      if (Object.keys(rest).length > 0) {
        scope.setExtras(rest);
      }

      applyApiTags(scope, normalizedPayload);
    }

    // Always tag the current browser route for non-API errors too
    if (!payload.route && typeof window !== "undefined") {
      scope.setTag("route", window.location.pathname);
    }

    const location = getStackLocation();
    if (Object.keys(location).length > 0) {
      scope.setContext("Caller", location);
      if (location.file) {
        scope.setTag("callerFile", location.file);
      }
    }

    // Normalize error-like plain objects so they always hit captureException
    const normalized = normalizeToError(message);
    const errorToCapture = normalized || message;

    if (errorToCapture instanceof Error) {
      const title = getErrorTitle(errorToCapture);
      scope.setExtra("errorName", errorToCapture.name);
      // Only prepend title if not already present to avoid double-prefixing
      if (!errorToCapture.message.startsWith(title)) {
        errorToCapture.message = `${title}: ${errorToCapture.message}`;
      }
      Sentry.captureException(errorToCapture);
    } else {
      const title = getErrorTitle(message);
      const msg = safeStringify(message);
      scope.setExtra("errorType", typeof message);
      scope.setExtra("originalMessage", message);
      Sentry.captureMessage(`${title}: ${msg}`, "error");
    }
  });
};

const logWithLevel = (level, message, user = null, payload = {}) => {
  if (message?.__sentryLogged) return;

  Sentry.withScope((scope) => {
    if (user) {
      scope.setUser({
        username: user?.name,
        email: user?.email,
        ip_address: "{{auto}}",
      });
    }

    const normalizedPayload = normalizePayload(payload);
    if (Object.keys(normalizedPayload).length > 0) {
      scope.setExtras(normalizedPayload);
      applyApiTags(scope, normalizedPayload);
    }

    if (typeof window !== "undefined") {
      scope.setTag("route", window.location.pathname);
    }

    const location = getStackLocation();
    if (Object.keys(location).length > 0) {
      scope.setContext("Caller", location);
      if (location.file) {
        scope.setTag("callerFile", location.file);
      }
    }

    // Normalize error-like plain objects so they always hit captureException
    const normalized = normalizeToError(message);
    const errorToCapture = normalized || message;

    if (errorToCapture instanceof Error) {
      const title = getErrorTitle(errorToCapture);
      scope.setExtra("errorName", errorToCapture.name);
      if (!errorToCapture.message.startsWith(title)) {
        errorToCapture.message = `${title}: ${errorToCapture.message}`;
      }
      Sentry.captureException(errorToCapture);
      return;
    }

    const title = getErrorTitle(message);
    const msg = safeStringify(message);
    scope.setExtra("errorType", typeof message);
    scope.setExtra("originalMessage", message);
    Sentry.captureMessage(`${title}: ${msg}`, level);
  });
};

export const log = async (error, user = null) => {
  logWithLevel("log", error, user);
};

export const logInfo = async (error, user = null) => {
  logWithLevel("info", error, user);
};

export const logWarning = async (error, user = null) => {
  logWithLevel("warning", error, user);
};

export const logDebug = async (error, user = null) => {
  logWithLevel("debug", error, user);
};

export const logError = async (error, user = null) => {
  logWithLevel("error", error, user);
};

export const logErrorWithPayload = async (error, payload = {}, user = null) => {
  logWithPayload(error, payload, user);
};

const logger = {
  init,
  log,
  logError,
  logInfo,
  logWarning,
  logDebug,
  setSentryUser,
};
export default logger;
